import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import Swal from "sweetalert2";
import {
  getDocs,
  serverTimestamp,
  setDoc,
  doc,
  onSnapshot,
} from "firebase/firestore";
import { v4 as uuidv4 } from "uuid";
import moment from "moment";
import TablaCotizaciones from "./TablaCotizaciones";
import { useNumeroConfig } from "../hooks/useNumeroConfig";
import { flushSync } from "react-dom";
import useArchivos from "../hooks/useArchivos";
import useProductos from "../hooks/useProductos";
import { useAuth } from "../context/AuthContext";
import { getSubCollection } from "../utils/getSubCollection";
import ModalCotizacion from "./ModalCotizacion";
import { notify } from "../components/NotificationsPortal";
import { orgCollection } from "../utils/firePaths";
import { db } from "../firebaseConfig";

moment.locale("es");

const diffInDays = (start, end) => {
  if (!start || !end) return null;
  const s = new Date(start);
  s.setHours(12, 0, 0, 0);
  const e = new Date(end);
  e.setHours(12, 0, 0, 0);
  const ms = e.getTime() - s.getTime();
  return Math.round(ms / (1000 * 60 * 60 * 24));
};

function CotizacionForm(props) {
  const {
    cotizacion,
    cotizaciones,
    onFormDirtyChange,
    closeModal,
    cloneMode = false,
    isOpen: isOpenProp,
  } = props;

  const [localOpen, setLocalOpen] = useState(isOpenProp ?? true);
  const isOpen = typeof isOpenProp === "boolean" ? isOpenProp : localOpen;

  useEffect(() => {
    if (typeof isOpenProp === "boolean") {
      setLocalOpen(isOpenProp);
    }
  }, [isOpenProp]);

  const { user, teamOwnerId, orgId } = useAuth();
  const ownerUid = teamOwnerId || user?.uid || null;
  const readyUid = ownerUid || "__LOADING__";
  const canRenderUI = Boolean(ownerUid || orgId);

  const initialDescMode =
    (cotizacion && cotizacion.tipoDescuentoGlobal) ||
    ((cotizacion?.descuento ?? 0) <= 100 ? "%" : "$");

  const {
    numero: numeroCotizacionAuto,
    modo: modoNumeroCotizacion,
    abrirConfiguracion,
    NumeroConfigModalUI,
  } = useNumeroConfig("numeroCotizacion", {
    title: "Editar folio",
    orgId,
    ownerUid: readyUid,
  });

  const [draftId, setDraftId] = useState(() => cotizacion?.id || uuidv4());
  const isEdit = Boolean(cotizacion?.id) && !cloneMode;
  const parentId = isEdit ? cotizacion.id : draftId;

  const { archivos } = useArchivos(
    "cotizaciones",
    canRenderUI ? parentId : "__LOADING__",
  );

  const [numeroSobrescrito, setNumeroSobrescrito] = useState(null);
  const [cliente, setCliente] = useState("");
  const [clientes, setClientes] = useState([]);
  const [catalogo, setCatalogo] = useState([]);
  const [tiempoEntrega, setTiempoEntrega] = useState("");
  const [condicionesPago, setCondicionesPago] = useState("");
  const [asunto, setAsunto] = useState("");
  const [fechaCotizacion, setFechaCotizacion] = useState(new Date());
  const [fechaVencimiento, setFechaVencimiento] = useState(null);
  const [vigenciaDias, setVigenciaDias] = useState(1);
  const [atencion, setAtencion] = useState("");
  const [clienteEmail, setClienteEmail] = useState("");
  const [clienteTelefono, setClienteTelefono] = useState("");
  const [divisa, setDivisa] = useState("MXN");
  const [ivaPct, setIvaPct] = useState(16);
  const [incoterm, setIncoterm] = useState("Seleccionar");
  const [area, setArea] = useState("");
  const [referencia, setReferencia] = useState("");
  const [terminos, setTerminos] = useState("");
  const [notas, setNotas] = useState("");
  const [vendedorNombre, setVendedorNombre] = useState("");
  const [vendedorCorreo, setVendedorCorreo] = useState("");
  const [vendedores, setVendedores] = useState([]);
  const [vendedorId, setVendedorId] = useState("");
  const [mostrarTabla] = useState(false);
  const [modoEdicion, setModoEdicion] = useState(false);
  const [invalidFields, setInvalidFields] = useState({
    folio: false,
    fecha: false,
    razonSocial: false,
    condicionesPago: false,
  });

  const [touchedReq, setTouchedReq] = useState({
    folio: false,
    fecha: false,
    razonSocial: false,
    condicionesPago: false,
  });

  const {
    datos,
    descuento,
    eliminar,
    actualizarCantidad,
    handleInputChange,
    iva,
    total,
    setDatos,
    setDescuento,
    descMode,
    subtotalBase,
  } = useProductos(
    cotizacion?.productosSeleccionados || [],
    cotizacion?.descuento || 0,
    catalogo,
    initialDescMode,
    ivaPct,
  );

  const markDirty = useCallback(() => {
    onFormDirtyChange?.(true);
  }, [onFormDirtyChange]);

  const addArticulo = useCallback(() => {
    setDatos((prev) => {
      const list = Array.isArray(prev) ? prev : [];
      return [
        ...list,
        {
          id: uuidv4(),
          nombre: "",
          sku: "",
          unidad: "Pieza",
          descripcion: "",
          numeroDeParteProveedor: "",
          cantidad: 1,
          tarifa: 0,
          iva: 0,
          descuento: 0,
          tipoDescuento: "%",
          importe: 0,
        },
      ];
    });
    markDirty();
  }, [setDatos, markDirty]);

  useEffect(() => {
    // si no hay ni ownerUid ni orgId, no podemos cargar nada
    if (!ownerUid && !orgId) return;

    const unsubs = [];

    // ✅ Clientes: si hay orgId, vienen de /orgs/{orgId}/clientes
    // si no hay orgId, fallback a /usuarios/{ownerUid}/clientes
    const clientesRef = orgId
      ? orgCollection(orgId, "clientes")
      : getSubCollection(ownerUid, "clientes");

    unsubs.push(
      onSnapshot(clientesRef, (snap) => {
        setClientes(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      }),
    );

    // ✅ Catálogo: mismo criterio
    const catalogoRef = orgId
      ? orgCollection(orgId, "productosClientes")
      : getSubCollection(ownerUid, "productosClientes");

    unsubs.push(
      onSnapshot(catalogoRef, (snap) => {
        setCatalogo(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      }),
    );

    // vendedores los dejo user-scope (requiere ownerUid)
    if (ownerUid) {
      const vendedoresCol = getSubCollection(
        ownerUid,
        "configuracion",
        "vendedores",
      );

      unsubs.push(
        onSnapshot(vendedoresCol, (snap) => {
          const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
          list.sort((a, b) =>
            String(a?.nombre || "").localeCompare(
              String(b?.nombre || ""),
              "es",
              {
                sensitivity: "base",
              },
            ),
          );
          setVendedores(list);
        }),
      );
    }

    return () => unsubs.forEach((u) => u());
  }, [ownerUid, orgId]);

  useEffect(() => {
    if (cotizacion && !cloneMode) {
      setModoEdicion(true);

      setTiempoEntrega(cotizacion.tiempoEntrega || "");
      setCondicionesPago(cotizacion.condicionesPago || "");
      setCliente(cotizacion.cliente || "");
      setReferencia(cotizacion.referencia || "");

      setFechaCotizacion(
        cotizacion.fechaCotizacion
          ? new Date(cotizacion.fechaCotizacion)
          : new Date(),
      );

      const fv = cotizacion.fechaVencimiento
        ? new Date(cotizacion.fechaVencimiento)
        : null;

      setFechaVencimiento(fv);

      const diff = diffInDays(
        cotizacion.fechaCotizacion
          ? new Date(cotizacion.fechaCotizacion)
          : new Date(),
        fv,
      );

      const allowed = [1, 7, 15, 30, 45, 60, 90];
      setVigenciaDias(allowed.includes(diff) ? diff : 1);

      setVendedorNombre(cotizacion.vendedorNombre || "");
      setVendedorCorreo(cotizacion.vendedorCorreo || "");
      setVendedorId(cotizacion?.vendedorId || "");

      setDatos(
        (cotizacion.productosSeleccionados || []).map((p) => ({
          id: uuidv4(),
          ...p,
          descripcion: p.descripcion ?? p.nombre,
          numeroDeParteProveedor: p.numeroDeParteProveedor || "",
          cantidad: p.cantidad ?? 1,
          tarifa: p.tarifa ?? 0,
          iva: p.iva ?? 0,
          descuento: p.descuento ?? 0,
          importe: p.importe ?? 0,
        })),
      );

      setDescuento(parseFloat(cotizacion.descuento) || 0);
      setAtencion(cotizacion.atencion || "");
      setClienteEmail(
        cotizacion.clienteEmail ?? cotizacion.email ?? cotizacion.correo ?? "",
      );
      setClienteTelefono(
        cotizacion.clienteTelefono ?? cotizacion.telefono ?? "",
      );
      setArea(cotizacion.area || "");
      setAsunto(cotizacion.asunto || "");
      setDivisa(cotizacion.divisa || "MXN");
      setNotas(cotizacion.notas || "");
      setTerminos(cotizacion.terminos || "");
      setIvaPct(
        Number.isFinite(Number(cotizacion?.ivaPct))
          ? Number(cotizacion.ivaPct)
          : 16,
      );
      setIncoterm(cotizacion?.incoterm || "Seleccionar");
      setNumeroSobrescrito(cotizacion.numeroCotizacion || null);
    } else if (cotizacion && cloneMode) {
      setModoEdicion(false);

      setNumeroSobrescrito(cotizacion.numeroCotizacion);
      setTiempoEntrega(cotizacion.tiempoEntrega || "");
      setCondicionesPago(cotizacion.condicionesPago || "");
      setCliente(cotizacion.cliente || "");
      setReferencia(cotizacion.referencia || "");

      setFechaCotizacion(
        cotizacion.fechaCotizacion
          ? new Date(cotizacion.fechaCotizacion)
          : new Date(),
      );

      const fv = cotizacion.fechaVencimiento
        ? new Date(cotizacion.fechaVencimiento)
        : null;

      setFechaVencimiento(fv);

      const diff = diffInDays(
        cotizacion.fechaCotizacion
          ? new Date(cotizacion.fechaCotizacion)
          : new Date(),
        fv,
      );

      const allowed = [1, 7, 15, 30, 45, 60, 90];
      setVigenciaDias(allowed.includes(diff) ? diff : 1);
      setVendedorNombre(cotizacion.vendedorNombre || "");
      setVendedorCorreo(cotizacion.vendedorCorreo || "");
      setVendedorId(cotizacion?.vendedorId || "");

      setDatos(
        (cotizacion.productosSeleccionados || []).map((p) => ({
          id: uuidv4(),
          ...p,
          descripcion: p.descripcion ?? p.nombre,
          numeroDeParteProveedor: p.numeroDeParteProveedor || "",
          cantidad: p.cantidad ?? 1,
          tarifa: p.tarifa ?? 0,
          iva: p.iva ?? 0,
          descuento: p.descuento ?? 0,
          importe: p.importe ?? 0,
        })),
      );

      setDescuento(parseFloat(cotizacion.descuento) || 0);
      setAtencion(cotizacion.atencion || "");
      setClienteEmail(
        cotizacion.clienteEmail ?? cotizacion.email ?? cotizacion.correo ?? "",
      );
      setClienteTelefono(
        cotizacion.clienteTelefono ?? cotizacion.telefono ?? "",
      );
      setArea(cotizacion.area || "");
      setAsunto(cotizacion.asunto || "");
      setDivisa(cotizacion.divisa || "MXN");
      setNotas(cotizacion.notas || "");
      setTerminos(cotizacion.terminos || "");
      setIvaPct(
        Number.isFinite(Number(cotizacion?.ivaPct))
          ? Number(cotizacion.ivaPct)
          : 16,
      );
      setIncoterm(cotizacion?.incoterm || "Seleccionar");
    } else {
      setModoEdicion(false);
      setNumeroSobrescrito(null);
      setTiempoEntrega("");
      setCondicionesPago("");
      setDatos([]);
      setDescuento(0);
      setCliente("");
      setAtencion("");
      setClienteEmail("");
      setClienteTelefono("");
      setArea("");
      setAsunto("");
      setDivisa("MXN");
      setNotas("");
      setTerminos("");
      setFechaCotizacion(new Date());
      setFechaVencimiento(null);
      setVigenciaDias(1);
      setIvaPct(16);
      setIncoterm("Seleccionar");
      setVendedorId("");
      setVendedorNombre("");
      setVendedorCorreo("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cotizacion, cloneMode]);

  const selectedCliente = useMemo(
    () => clientes.find((x) => x.id === cliente) || null,
    [clientes, cliente],
  );

  useEffect(() => {
    if (!selectedCliente) {
      setClienteEmail("");
      setClienteTelefono("");
      setAtencion("");
      return;
    }

    const email = selectedCliente?.email || selectedCliente?.correo || "";
    const tel = selectedCliente?.telefono || "";

    const att =
      selectedCliente?.atencion ||
      selectedCliente?.contacto ||
      selectedCliente?.persona ||
      selectedCliente?.nombreContacto ||
      "";

    setClienteEmail(email);
    setClienteTelefono(tel);
    setAtencion(att);
  }, [selectedCliente]);

  const resetForm = useCallback(() => {
    setNumeroSobrescrito(null);
    setTiempoEntrega("");
    setCondicionesPago("");
    setDatos([]);
    setDescuento(0);
    setCliente("");
    setAtencion("");
    setClienteEmail("");
    setClienteTelefono("");
    setArea("");
    setAsunto("");
    setDivisa("MXN");
    setNotas("");
    setTerminos("");
    setFechaCotizacion(new Date());
    setFechaVencimiento(null);
    setVigenciaDias(1);
    setIvaPct(16);
    setIncoterm("Seleccionar");
    setVendedorId("");
    setVendedorNombre("");
    setVendedorCorreo("");
    setInvalidFields({
      folio: false,
      fecha: false,
      razonSocial: false,
      condicionesPago: false,
    });
    setTouchedReq({
      folio: false,
      fecha: false,
      razonSocial: false,
      condicionesPago: false,
    });
  }, [setDatos, setDescuento]);

  const prevOpenRef = useRef(false);

  useEffect(() => {
    const wasOpen = prevOpenRef.current;
    prevOpenRef.current = Boolean(isOpen);

    if (!wasOpen && isOpen && !cotizacion) {
      resetForm();
      setDraftId(uuidv4());
    }
  }, [isOpen, cotizacion, resetForm]);

  const handleCancel = useCallback(() => {
    resetForm();
    setLocalOpen(false);
    closeModal?.();
    onFormDirtyChange?.(false);
  }, [closeModal, onFormDirtyChange, resetForm]);

  const guardar = async (e) => {
    e.preventDefault();

    if (!orgId && !ownerUid) {
      await Swal.fire({
        icon: "error",
        title: "Sin sesión / organización",
        text: "No se detectó orgId / ownerUid. Recarga e inicia sesión.",
      });
      return;
    }

    if (!orgId) {
      await Swal.fire({
        icon: "error",
        title: "Sin organización",
        text: "No se detectó orgId. No se puede guardar la cotización en la nueva estructura.",
      });
      return;
    }

    const nNum = (x, def = 0) => (Number.isFinite(x) ? x : def);
    const nStr = (x, def = "") => x ?? def;

    const normalizeProducto = (p) => {
      const cantidad = nNum(p?.cantidad, 1);
      const tarifa = nNum(p?.tarifa, 0);

      const tipoDescuento = p?.tipoDescuento ?? "%";
      const descValor = nNum(p?.descuento, 0);

      const base = tarifa * cantidad;
      let descLinea =
        tipoDescuento === "%" ? base * (descValor / 100) : descValor;

      if (descLinea > base) descLinea = base;

      const importe = base - descLinea;

      return {
        id: nStr(p?.id, uuidv4()),
        sku: nStr(
          p?.sku ||
            p?.SKU ||
            p?.codigo ||
            p?.clave ||
            p?.numeroDeParteProveedor ||
            p?.modelo ||
            "",
        ),
        unidad: nStr(p?.unidad || p?.um || p?.medida || "Pieza"),
        nombre: nStr(p?.nombre),
        descripcion: nStr(p?.descripcion ?? p?.nombre),
        modelo: nStr(p?.modelo ?? p?.numeroDeParteCliente),
        numeroDeParteProveedor: nStr(p?.numeroDeParteProveedor),
        cantidad,
        tarifa,
        tipoDescuento,
        descuento: descValor,
        importe,
        iva: nNum(p?.iva, 0),
      };
    };

    const normalizeArchivo = (a) => ({
      id: nStr(a?.id),
      nombre: nStr(a?.nombre),
      url: nStr(a?.url),
      path: nStr(a?.path),
      size: Number.isFinite(a?.size) ? a.size : null,
      type: nStr(a?.type),
    });

    const pruneUndefinedDeep = (val) => {
      if (Array.isArray(val)) return val.map(pruneUndefinedDeep);
      if (val && typeof val === "object") {
        const out = {};
        for (const [k, v] of Object.entries(val)) {
          if (v === undefined) continue;
          out[k] = pruneUndefinedDeep(v);
        }
        return out;
      }
      return val;
    };

    const numeroEfectivo = numeroSobrescrito ?? numeroCotizacionAuto;
    const folioVal = String(numeroEfectivo || "").trim();
    const fechaVal = fechaCotizacion ? new Date(fechaCotizacion) : null;
    const fechaOk = fechaVal instanceof Date && !isNaN(fechaVal.getTime());

    const missing = {
      folio: folioVal === "" || !touchedReq.folio || /^0+$/.test(folioVal),
      fecha: !fechaOk || !touchedReq.fecha,
      razonSocial: !cliente || !touchedReq.razonSocial,
      condicionesPago:
        String(condicionesPago || "").trim() === "" ||
        !touchedReq.condicionesPago,
    };

    if (
      missing.folio ||
      missing.fecha ||
      missing.razonSocial ||
      missing.condicionesPago
    ) {
      setInvalidFields(missing);

      const faltantes = [];
      if (missing.folio) faltantes.push("Folio");
      if (missing.fecha) faltantes.push("Fecha");
      if (missing.razonSocial) faltantes.push("Razón social");
      if (missing.condicionesPago) faltantes.push("Condiciones de pago");

      const msg = `Faltan campos por llenar: ${faltantes.join(", ")}`;

      notify({
        type: "error",
        message: msg,
        duration: 4500,
      });

      return;
    }

    try {
      const cotCol = orgCollection(orgId, "cotizaciones");
      const snap = await getDocs(cotCol);
      const dup = snap.docs.find(
        (d) => d.data().numeroCotizacion === numeroEfectivo,
      );

      if (dup && (!modoEdicion || dup.id !== cotizacion?.id)) {
        await Swal.fire({
          icon: "error",
          title: "Número duplicado",
          text: `Ya existe la cotización “${numeroEfectivo}”.`,
        });
        return;
      }

      const sel = clientes.find((c) => c.id === cliente) || {};
      const nombreCliente = sel.empresa || sel.nombre || "";
      const clienteRfc = sel?.rfc || sel?.clienteRfc || "";

      const clienteContacto =
        atencion ||
        sel?.atencion ||
        sel?.contacto ||
        sel?.persona ||
        sel?.nombreContacto ||
        "";

      const domicilio =
        sel?.domicilio ||
        sel?.direccion ||
        sel?.direccionCliente ||
        sel?.calle ||
        sel?.direccionFiscal ||
        "";

      const numeroExt =
        sel?.numeroExt ||
        sel?.noExt ||
        sel?.numeroExterior ||
        sel?.exterior ||
        "";

      const numeroInt =
        sel?.numeroInt ||
        sel?.noInt ||
        sel?.numeroInterior ||
        sel?.interior ||
        "";

      const colonia = sel?.colonia || sel?.col || sel?.barrio || "";

      const codigoPostal = sel?.codigoPostal || sel?.cp || sel?.postal || "";

      const ciudad =
        sel?.ciudad || sel?.municipio || sel?.poblacion || sel?.localidad || "";

      const clienteLinea1 = [
        domicilio,
        numeroExt ? `#${numeroExt}` : "",
        numeroInt ? `Int ${numeroInt}` : "",
      ]
        .filter(Boolean)
        .join(" ")
        .trim();

      const clienteLinea2 = [
        colonia,
        ciudad,
        codigoPostal ? `CP ${codigoPostal}` : "",
      ]
        .filter(Boolean)
        .join(", ")
        .trim();

      const direccionCliente = [clienteLinea1, clienteLinea2]
        .filter(Boolean)
        .join(", ")
        .trim();
      const productosLimpios = (datos || []).map(normalizeProducto);
      const archivosLimpios = (archivos || []).map(normalizeArchivo);
      const creadoPorUid = nStr(user?.uid || "");
      const creadoPorNombre = nStr(user?.displayName || user?.email || "");
      const creadoPorEmail = nStr(user?.email || "");

      const baseData = {
        cliente,
        nombreCliente,
        clienteLinea1: nStr(clienteLinea1),
        clienteLinea2: nStr(clienteLinea2),
        clienteRfc: nStr(clienteRfc),
        direccionCliente: nStr(direccionCliente),
        numeroCotizacion: numeroEfectivo,
        clienteEmail: nStr(clienteEmail),
        clienteTelefono: nStr(clienteTelefono),
        referencia,
        fechaCotizacion: fechaCotizacion.toISOString(),
        fechaVencimiento: fechaVencimiento
          ? fechaVencimiento.toISOString()
          : null,
        vendedorNombre: nStr(vendedorNombre),
        vendedorCorreo: nStr(vendedorCorreo),
        vendedorId: nStr(vendedorId),
        atencion: nStr(clienteContacto),
        area: nStr(area),
        divisa,
        ivaPct: Number.isFinite(Number(ivaPct)) ? Number(ivaPct) : 16,
        incoterm: nStr(incoterm, "Seleccionar"),
        asunto: nStr(asunto),
        productosSeleccionados: productosLimpios,
        subtotalBase: nNum(subtotalBase, 0),
        subtotalConDesc: nNum(total - iva, 0),
        iva: nNum(iva, 0),
        total: nNum(total, 0),
        notas: nStr(notas),
        terminos: nStr(terminos),
        tiempoEntrega: nStr(tiempoEntrega),
        condicionesPago: nStr(condicionesPago),
        descuento: nNum(descuento, 0),
        tipoDescuentoGlobal: descMode,
        estado: "Pendiente",
        archivos: archivosLimpios,
        rfc: nStr(clienteRfc),
        correo: nStr(clienteEmail),
        telefono: nStr(clienteTelefono),
        contactoPersonal: nStr(clienteContacto),
        clienteContacto: nStr(clienteContacto),
        domicilio: nStr(domicilio),
        numeroExt: nStr(numeroExt),
        numeroInt: nStr(numeroInt),
        colonia: nStr(colonia),
        codigoPostal: nStr(codigoPostal),
        ciudad: nStr(ciudad),
        ...(modoEdicion
          ? {}
          : {
              createdAt: serverTimestamp(),
              creadoPorUid,
              creadoPorNombre,
              creadoPorEmail,
            }),
      };

      const data = pruneUndefinedDeep(baseData);

      if (modoEdicion && cotizacion?.id) {
        const docRef = doc(cotCol, cotizacion.id);
        await setDoc(docRef, data, { merge: true });
      } else {
        const docRef = doc(cotCol, draftId);
        await setDoc(docRef, data);

        if (modoNumeroCotizacion === "auto") {
          const match = numeroEfectivo.match(/^(.*-)?(\d+)$/);
          if (match) {
            const [, prefijo = "", raw] = match;
            const width = String(raw).length || 1;
            const siguienteNumero = parseInt(raw, 10) + 1;

            const configRef = doc(
              db,
              "orgs",
              orgId,
              "settings",
              "numeroCotizacion",
            );

            await setDoc(
              configRef,
              {
                modo: "auto",
                prefijo: (prefijo || "").replace(/-$/, ""),
                siguienteNumero,
                padding: width,
                ultimoNumeroUsado: Math.max(0, siguienteNumero - 1),
              },
              { merge: true },
            );
          }
        }
      }

      notify({
        type: "success",
        message: `Cotización ${numeroEfectivo} ${
          modoEdicion ? "actualizada" : "creada"
        } exitosamente`,
        duration: 3500,
      });

      flushSync(() => {
        onFormDirtyChange?.(false);
      });

      setLocalOpen(false);
      closeModal?.();
    } catch (err) {
      console.error(err);
      await Swal.fire({
        icon: "error",
        title: "Error al guardar",
        text: err.message,
      });
    }
  };

  const numeroEfectivo = numeroSobrescrito ?? numeroCotizacionAuto;

  return (
    <>
      {canRenderUI ? (
        <>
          <ModalCotizacion
            key={cotizacion?.id ? `edit-${cotizacion.id}` : `new-${draftId}`}
            isOpen={isOpen}
            onRequestClose={handleCancel}
            mode={cotizacion ? (cloneMode ? "clone" : "edit") : "create"}
            onSubmit={guardar}
            onCancel={handleCancel}
            markDirty={markDirty}
            numeroEfectivo={numeroEfectivo}
            setNumeroSobrescrito={(v) => {
              setNumeroSobrescrito(v);
              setInvalidFields((p) => ({ ...p, folio: false }));
              setTouchedReq((p) => ({ ...p, folio: true }));
            }}
            abrirConfiguracion={abrirConfiguracion}
            fechaCotizacion={fechaCotizacion}
            setFechaCotizacion={(v) => {
              setFechaCotizacion(v);
              setInvalidFields((p) => ({ ...p, fecha: false }));
              setTouchedReq((p) => ({ ...p, fecha: true }));
            }}
            fechaVencimiento={fechaVencimiento}
            setFechaVencimiento={setFechaVencimiento}
            vigenciaDias={vigenciaDias}
            setVigenciaDias={setVigenciaDias}
            divisa={divisa}
            setDivisa={setDivisa}
            ivaPct={ivaPct}
            setIvaPct={setIvaPct}
            incoterm={incoterm}
            setIncoterm={setIncoterm}
            vendedores={vendedores}
            vendedorId={vendedorId}
            setVendedorId={setVendedorId}
            setVendedorNombre={setVendedorNombre}
            setVendedorCorreo={setVendedorCorreo}
            asunto={asunto}
            setAsunto={setAsunto}
            clientes={clientes}
            cliente={cliente}
            setCliente={(v) => {
              setCliente(v);
              setInvalidFields((p) => ({ ...p, razonSocial: false }));
              setTouchedReq((p) => ({ ...p, razonSocial: true }));
            }}
            selectedCliente={selectedCliente}
            atencion={atencion}
            setAtencion={setAtencion}
            clienteEmail={clienteEmail}
            setClienteEmail={setClienteEmail}
            clienteTelefono={clienteTelefono}
            setClienteTelefono={setClienteTelefono}
            datos={datos}
            addArticulo={addArticulo}
            eliminar={eliminar}
            actualizarCantidad={actualizarCantidad}
            handleInputChange={handleInputChange}
            setDatos={setDatos}
            subtotalBase={subtotalBase}
            iva={iva}
            total={total}
            condicionesPago={condicionesPago}
            setCondicionesPago={(v) => {
              setCondicionesPago(v);
              setInvalidFields((p) => ({ ...p, condicionesPago: false }));
              setTouchedReq((p) => ({ ...p, condicionesPago: true }));
            }}
            tiempoEntrega={tiempoEntrega}
            setTiempoEntrega={setTiempoEntrega}
            notas={notas}
            setNotas={setNotas}
            invalidFields={invalidFields}
          />

          {mostrarTabla && (
            <TablaCotizaciones
              numeroCotizacion={numeroEfectivo}
              cotizaciones={cotizaciones}
            />
          )}

          <NumeroConfigModalUI />
        </>
      ) : null}
    </>
  );
}

export default CotizacionForm;
